import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Paper,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { GridColDef } from "@mui/x-data-grid-pro";
import { v4 as uuidv4 } from "uuid";
import {
  formatNumber,
  formatTwoDecimal,
  validateTextField,
} from "../../../../../../../../utils/helpers";
import { setEditScopeLoading } from "../../../../../../../../features/scopesTabSlice";
import { useDispatch } from "react-redux";
import { STOperationEndPointLogic } from "../../../../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../../../features/userSlice";
import { useScopes } from "../../../../STOperationhooks/useScopes";
import CommonDataGridTable from "../../../../CommonDataGridTable/CommonDataGridTable";
import { intersection, not } from "../../../../utilsSearchTerm/helperST";

interface BulkActionModalProps {
  open: boolean;
  onClose: () => void;
}

const EditScopeModal = ({ open, onClose }: BulkActionModalProps) => {
  const dispatch = useDispatch();
  const [scopeName, setScopeName] = useState("");
  const [scopeSharedUid, setScopeSharedUid] = useState("");
  const [existingScopeName, setExistingScopeName] = useState("");
  const [error, setError] = useState("");
  const [campaignError, setCampaignError] = useState("");
  const [assignedCampaigns, setAssignedCampaigns] = useState<any[]>([]);
  const [unAssignedCampaigns, setUnAssignedCampaigns] = useState<any[]>([]);

  // --- Transfer List state ---
  const [checked, setChecked] = useState<number[]>([]);
  const [left, setLeft] = useState<any[]>([]);
  const [right, setRight] = useState<any[]>([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const {
    campaignsWithoutScopeData,
    // negativesInTheCampaignData,
    newScopeLoading,
    editScopeLoading,
    campaignToScopeLoading,
    selectedSingleScopeID,
    scopesOfSTOperation,
  } = useSelector((state: any) => state.searchTermOperation);
  const { campaignsWithoutScope, scopeList, linkCampaignToScope } = useScopes();

  const oauthTokenHeader = useSelector(selectUserToken) as any;

  const filteredCampaigns = (campaignsWithoutScopeData ?? []).map(
    (campaign: any) => {
      return {
        campaign_shared_uid: campaign.campaign_shared_uid,
        campaign_name: campaign.campaign_name,
      };
    }
  );

  // Get scope details by ID
  const scopeDetails = (scopeID: string | any) => {
    return (scopesOfSTOperation ?? []).find(
      (scope: any) => scope.shared_uid === scopeID
    );
  };

  //get existing scope details
  const getLinkedCampaignsForCurrentScope = (scopeID: string) => {
    const getScopeDetails = scopeDetails(scopeID);
    if (!getScopeDetails) return [];
    const capaigns = getScopeDetails?.campaigns || [];
    return capaigns
      .filter((campaign: any) => campaign.mapping_is_active === true)
      .map((campaign: any) => {
        return {
          campaign_shared_uid: campaign.campaign_shared_uid,
          campaign_name: campaign.campaign_name,
          campaign_to_scope_shared_uid: campaign.shared_uid,
        };
      });
  };
  const alreadyLinkedCampaigns = getLinkedCampaignsForCurrentScope(
    selectedSingleScopeID
  ); // get already linked campaigns

  //--when modal open set left with all campaigns and right with scope details(if present)--
  useEffect(() => {
    if (!open) return;
    const detailsOfScope = scopeDetails(selectedSingleScopeID);
    setScopeName(detailsOfScope?.name || "");
    setScopeSharedUid(detailsOfScope?.shared_uid || "");
    setExistingScopeName(detailsOfScope?.name || "");

    if (filteredCampaigns?.length > 0) {
      setLeft(filteredCampaigns); // set left with all campaigns
      setUnAssignedCampaigns(filteredCampaigns);
    } else {
      setLeft([]); // set left empty if no unassigned campaigns
      setUnAssignedCampaigns([]);
    }

    if (alreadyLinkedCampaigns?.length > 0) {
      setRight(alreadyLinkedCampaigns); // set right with scope details if present
      setAssignedCampaigns(alreadyLinkedCampaigns);
    } else {
      setRight([]); // set right empty if no assigned campaigns
      setAssignedCampaigns([]);
    }

    //eslint-disable-next-line
  }, [open]);

  //get campaign details for the campaigns in both list (right and left list)
  // const campaignsWithDetails = useMemo(() => {
  //   // If both side is empty → nothing to map
  //   if (!left?.length && !right?.length) return [];

  //   // Combine both lists
  //   const bothSideCampaigns = [...left, ...right];

  //   // If no campaign details exist, return empty
  //   if (!negativesInTheCampaignData?.length) return [];

  //   // Map and merge details
  //   return bothSideCampaigns
  //     .map((campaign: any) => {
  //       const details = negativesInTheCampaignData.find(
  //         (d: any) =>
  //           d.campaign_shared_uid === campaign.campaign_shared_uid &&
  //           d.campaign_name === campaign.campaign_name
  //       );

  //       return details
  //         ? {
  //             id: uuidv4(),
  //             campaign_name: details.campaign_name,
  //             campaign_id: details.campaign_id,
  //             clicks_last_3_years: details.clicks_last_3_years,
  //             cost_last_30_days: details.cost_last_30_days,
  //             convertions_last_30_days: details.convertions_last_30_days,
  //           }
  //         : null;
  //     })
  //     .filter(Boolean); // remove nulls
  // }, [left, right, negativesInTheCampaignData]);

  //get campaign details for the campaigns in left list(According to current requirements)
  const campaignsWithDetails =
    (campaignsWithoutScopeData?.length ?? 0) > 0
      ? campaignsWithoutScopeData.map((details: any) => ({
          id: uuidv4(),
          campaign_shared_uid: details.campaign_shared_uid || "",
          campaign_name: details.campaign_name || "",
          campaign_id: details.campaign_id || "",
          clicks_last_30_days: details.clicks_last_30_days || "",
          cost_last_30_days: details.cost_last_30_days || "",
          convertions_last_30_days: details.convertions_last_30_days || "",
        }))
      : [];

  const handleToggle = (id: number) => () => {
    const currentIndex = checked.indexOf(id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(id);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(
      not(
        checked,
        leftChecked.map((i) => i.campaign_shared_uid)
      )
    );
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(
      not(
        checked,
        rightChecked.map((i) => i.campaign_shared_uid)
      )
    );
  };

  //--close modal--
  const handleClose = () => {
    setScopeName(""); // clear scope name
    setError(""); // clear scope name error
    setCampaignError(""); // clear campaign error
    setChecked([]); // clear checked
    setLeft(filteredCampaigns); // reset left with all campaigns
    setRight(alreadyLinkedCampaigns); // reset right with linked campaigns
    onClose(); // call parent onClose
  };

  //--unlink campaign from scope--
  const unlinkCampaigns = async (campaignIds: any[]) => {
    const response =
      await STOperationEndPointLogic.unlinkCampaigsFromCurrentScope(
        oauthTokenHeader,
        { campaign_to_scope_shared_uid: campaignIds }
      );

    const reason = response?.reason;

    // --- CASE 1: All success (204) ---
    if (reason === "success") {
      // Refresh lists
      await campaignsWithoutScope();
      await scopeList();
      dispatch(setEditScopeLoading(false));
      dispatch(
        setDisplayErrorSnackBar({
          message: `${campaignIds.length} campaign(s) unlinked successfully`,
          type: "success",
        })
      );
      return;
    }

    // --- CASE 2: Partial success (207) ---
    if (response?.partialSuccess) {
      const successCount = response.successCount || 0;
      const failCount = response.failureCount || 0;

      if (successCount > 0 && failCount > 0) {
        // Refresh lists
        await campaignsWithoutScope();
        await scopeList();
        dispatch(setEditScopeLoading(false));
        dispatch(
          setDisplayErrorSnackBar({
            message: `${successCount} campaign(s) unlinked, ${failCount} failed`,
            type: "warning",
          })
        );
        return;
      }

      // --- CASE 3: All failed (207 but all unlink: false) ---
      if (successCount === 0 && failCount > 0) {
        dispatch(setEditScopeLoading(false));
        const firstFailureReason = response.failedUnlinks?.[0]?.reason;
        dispatch(
          setDisplayErrorSnackBar({
            message:
              reason || firstFailureReason || "Failed to unlink campaigns",
            type: "error",
          })
        );
        return;
      }
    }

    // --- CASE 4: Unexpected or error response ---
    dispatch(setEditScopeLoading(false));
    dispatch(
      setDisplayErrorSnackBar({
        message: reason || "Unexpected response while unlinking campaigns.",
        type: "error",
      })
    );
  };

  const updateScope = async (
    scopeName: string,
    assignedCampaignsToUnlink?: any[],
    unAssignedCampaignsToLink?: any[]
  ) => {
    const requestParams = {
      name: scopeName,
    };
    const campaignsToUnlink = assignedCampaignsToUnlink ?? []; // this campaigns will be unlinked
    const campaignsToLink = unAssignedCampaignsToLink ?? []; // this campaigns will be linked
    try {
      handleClose(); // close modal on submit (dont wait for response as it takes time)
      dispatch(setEditScopeLoading(true));

      //update the existing scope name if scope name is changed
      if (
        scopeName.trim().toLowerCase() !==
        existingScopeName.trim().toLowerCase()
      ) {
        //update the existing scope name
        const editResp = await STOperationEndPointLogic.updateExistingScope(
          oauthTokenHeader,
          requestParams,
          selectedSingleScopeID
        );

        if (!editResp || editResp.reason !== "success" || !editResp.data) {
          throw new Error(editResp?.reason || "Failed to update scope");
        }
      }

      // Unlink campaigns if needed
      if (campaignsToUnlink.length > 0) {
        await unlinkCampaigns(campaignsToUnlink);
      }

      // Link campaigns if needed
      if (campaignsToLink.length > 0) {
        const linkReqBody = {
          scope_shared_uid: scopeSharedUid,
          campaign_shared_uid: campaignsToLink,
        };
        await linkCampaignToScope(linkReqBody, "EditScope");
      }
    } catch (error) {
      dispatch(setEditScopeLoading(false));
    }
  };

  // Function to handle confirm
  const handleConfirm = () => {
    const validationError = validateTextField(scopeName, 8);
    if (validationError) {
      setError(validationError);
      return;
    }

    setCampaignError("");

    //find campaigns from the left-side list that are already linked, to be unlinked from the current scope
    const linkedCampaignsToUnlink =
      left
        .filter((left) =>
          assignedCampaigns.some(
            (assigned) =>
              assigned.campaign_shared_uid === left.campaign_shared_uid
          )
        )
        .map((l) => l.campaign_to_scope_shared_uid) || []; // get campaign_to_scope_shared_uid array

    //find campaigns from the right-side list that are not linked, to be linked with the current scope
    const unlinkedCampaignsToLink =
      right
        .filter((right) =>
          unAssignedCampaigns.some(
            (unAssigned) =>
              unAssigned.campaign_shared_uid === right.campaign_shared_uid
          )
        )
        .map((l) => l.campaign_shared_uid) || []; // get campaign shared_uid array

    // Check if scope name has changed
    const isScopeNameChanged =
      scopeName.trim().toLowerCase() !== existingScopeName.trim().toLowerCase();

    // Check if there are any changes
    const hasChanges =
      isScopeNameChanged ||
      linkedCampaignsToUnlink?.length > 0 ||
      unlinkedCampaignsToLink?.length > 0;

    // If no changes, show message and return
    if (!hasChanges) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "No changes detected. Please make changes before saving.",
          type: "warning",
        })
      );
      return;
    }

    //check if any campaign is being linked or unlinked then edit process is not allowed
    if (
      linkedCampaignsToUnlink?.length > 0 ||
      unlinkedCampaignsToLink?.length > 0
    ) {
      if (newScopeLoading || campaignToScopeLoading) {
        dispatch(
          setDisplayErrorSnackBar({
            message:
              "Campaign linking or unlinking is in progress. Please wait until it’s complete before starting another.",
            type: "warning",
          })
        );
        return;
      }
    }

    //api call
    updateScope(scopeName, linkedCampaignsToUnlink, unlinkedCampaignsToLink);
  };

  // === COLUMNS ===
  const columns: GridColDef[] = [
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "campaign_id",
      headerName: "Campaign ID",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "clicks_last_30_days",
      headerName: "Clicks Last 30 Days",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatNumber(params.value) : ""}
        </Typography>
      ),
    },
    {
      field: "cost_last_30_days",
      headerName: "Cost Last 30 Days",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatTwoDecimal(params.value, "$")}
        </Typography>
      ),
    },
    {
      field: "convertions_last_30_days",
      headerName: "Conversions Last 30 Days",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatTwoDecimal(params.value, "%")}
        </Typography>
      ),
    },
  ];

  const customList = (items: any[]) => (
    <Paper sx={{ width: 350, height: 300, overflow: "auto" }}>
      <List dense component="div" role="list">
        {items.map((value: any) => {
          const labelId = `transfer-list-item-${value.campaign_shared_uid}-label`;

          return (
            <ListItem
              key={value.campaign_shared_uid}
              role="listitem"
              disablePadding
            >
              <ListItemButton
                onClick={handleToggle(value.campaign_shared_uid)}
                disabled={editScopeLoading}
              >
                <ListItemIcon>
                  <Checkbox
                    color="secondary"
                    checked={checked.indexOf(value.campaign_shared_uid) !== -1}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    inputProps={{ "aria-labelledby": labelId }}
                    disabled={editScopeLoading}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value.campaign_name} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ padding: "20px 20px 0px 16px" }}>
        Edit Scope
      </DialogTitle>
      <DialogContent sx={{ padding: "0px 20px 16px", position: "relative" }}>
        {/* Scope Name Input */}
        <TextField
          required
          label="Scope Name"
          value={scopeName}
          onChange={(e) => {
            setScopeName(e.target.value);
            const err = validateTextField(e.target.value, 8);
            setError(err);
          }}
          color="secondary"
          sx={{ width: "100%", marginBottom: 3, mt: 2 }}
          size="small"
          error={!!error}
          helperText={
            error || "Minimum of 8 characters, only letters and numbers."
          }
        />

        {/* Transfer List Section */}
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item>
            <Typography gutterBottom color="text.secondary" mb={1.5}>
              All available Campaigns
            </Typography>
            {customList(left)}
          </Grid>
          <Grid item>
            <Grid container direction="column" alignItems="center">
              <Button
                sx={{ my: 0.5 }}
                color="secondary"
                variant="outlined"
                size="small"
                onClick={handleAllRight}
                disabled={left.length === 0}
              >
                ≫
              </Button>
              <Button
                sx={{ my: 0.5 }}
                color="secondary"
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
              >
                &lt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleAllLeft}
                disabled={right.length === 0}
              >
                ≪
              </Button>
            </Grid>
          </Grid>
          <Grid item>
            <Typography gutterBottom color="text.secondary" mb={1.5}>
              Campaigns In new scope
            </Typography>
            {customList(right)}

            {campaignError && (
              <Typography color="error" variant="body2" sx={{ mt: 1, ml: 2 }}>
                {campaignError}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ my: 3, fontStyle: "italic", display: "block" }}
        >
          * The left list shows campaigns not yet assigned to any scope, while
          the right list shows campaigns linked to this scope. Use the buttons
          in the middle to move campaigns between the lists. When you unlink a
          campaign, it moves to the left list from the right; when you link one,
          it appears on the right list from the left list.
        </Typography>

        {/* table */}
        <Typography sx={{ mt: 3, mb: 1 }} variant="subtitle1">
          Campaign Details
        </Typography>
        <CommonDataGridTable rows={campaignsWithDetails} columns={columns} />
      </DialogContent>

      <DialogActions sx={{ padding: "0px 16px 20px 16px" }}>
        <Button
          onClick={handleClose}
          disabled={editScopeLoading}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          data-tracker="500"
          disabled={!scopeName || !!error || editScopeLoading}
          onClick={handleConfirm}
          color="secondary"
          variant="contained"
          endIcon={<AddIcon />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditScopeModal;
